import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import type { Doctor, Visit, Apoio, Period } from "@/types";
import { PRIO_LABELS, POT_LABELS, APOIO_LABELS, DAYS, PRIO_TARGET } from "@/lib/constants";
import { calcScore, calcCompliance, nextVisitDate } from "@/lib/scoring";

function fmtDate(d: string) {
  if (!d) return "-";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
}

function ScoreRing({ score, barColor, pct }: { score: number; barColor: string; pct: number }) {
  const r = 29;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct / 100);
  const color = score >= 70 ? "var(--mv-green)" : score >= 40 ? "var(--mv-orange)" : "var(--mv-red)";

  return (
    <div className="relative flex-shrink-0" style={{ width: 70, height: 70 }}>
      <svg width="70" height="70" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="35" cy="35" r={r} fill="none" stroke="var(--mv-border)" strokeWidth="5" />
        <circle
          cx="35" cy="35" r={r} fill="none"
          stroke={barColor} strokeWidth="5" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.6s" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center font-mono">
        <span className="text-[1.1rem] font-semibold leading-none" style={{ color }}>{score}</span>
        <span className="text-[0.48rem] uppercase tracking-wider" style={{ color: "var(--mv-ink-light)" }}>Score</span>
      </div>
    </div>
  );
}

function NextVisitBadge({ date }: { date: string | null }) {
  if (!date) return <span className="font-mono text-[0.7rem] px-[10px] py-1 rounded-full" style={{ background: "var(--mv-surface2)", color: "var(--mv-ink-light)" }}>Sem visitas ainda</span>;
  const today = new Date().toISOString().slice(0, 10);
  const inDays = Math.ceil((new Date(date).getTime() - Date.now()) / 86400000);
  let style: React.CSSProperties;
  let label: string;
  if (date < today) {
    style = { background: "var(--mv-red-light)", color: "var(--mv-red)" };
    label = `Atrasado (${fmtDate(date)})`;
  } else if (inDays <= 7) {
    style = { background: "var(--mv-orange-light)", color: "var(--mv-orange)" };
    label = `Em breve (${fmtDate(date)})`;
  } else {
    style = { background: "var(--mv-green-light)", color: "var(--mv-green-dark)" };
    label = fmtDate(date);
  }
  return (
    <span className="font-mono text-[0.7rem] px-[10px] py-1 rounded-full inline-flex items-center gap-[5px]" style={style}>
      {label}
    </span>
  );
}

export function DoctorDetailModal({
  doctor,
  visits,
  period,
  apoios,
  onClose,
  onEdit,
  onDelete,
}: {
  doctor: Doctor;
  visits: Record<string, Visit[]>;
  period: Period;
  apoios: Apoio[];
  onClose: () => void;
  onEdit: () => void;
  onDelete?: () => void;
}) {
  const score = calcScore(doctor, visits, period);
  const compliance = calcCompliance(doctor, visits, period);
  const nextVisit = nextVisitDate(doctor, visits, period);
  const pct = Math.min(100, Math.round((compliance.actual / compliance.target) * 100));
  const barColor = compliance.status === "ok" ? "var(--mv-green)" : compliance.actual > 0 ? "var(--mv-orange)" : "var(--mv-red)";

  const doctorVisits = useMemo(() => {
    const all: (Visit & { dateKey: string })[] = [];
    for (const [date, dayVisits] of Object.entries(visits)) {
      for (const v of dayVisits) {
        if (v.doctorId === doctor.id) all.push({ ...v, dateKey: date });
      }
    }
    return all.sort((a, b) => b.dateKey.localeCompare(a.dateKey)).slice(0, 20);
  }, [visits, doctor.id]);

  const doctorApoios = useMemo(
    () => apoios.filter((a) => a.medicoId === doctor.id).sort((a, b) => b.dataInicio.localeCompare(a.dataInicio)),
    [apoios, doctor.id]
  );

  const subtitle = [doctor.espec, doctor.local].filter(Boolean).join(" · ");

  const modal = (
    <div
      className="fixed inset-0 z-[600] flex flex-col items-center px-4"
      style={{ background: "rgba(15,46,90,0.5)", backdropFilter: "blur(3px)", paddingTop: 20, paddingBottom: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-[740px] flex flex-col min-h-0 max-h-full"
        style={{
          background: "var(--mv-surface)",
          borderRadius: 16,
          border: "1px solid var(--mv-border)",
          boxShadow: "0 20px 60px rgba(15,46,90,0.25)",
          animation: "modalIn 0.22s ease",
        }}
      >
        {/* Header */}
        <div className="mv-panel-header shrink-0 justify-between">
          <div>
            <div className="mv-panel-title">{doctor.name}</div>
            <div className="text-[0.7rem]" style={{ color: "var(--mv-ink-light)" }}>{subtitle}</div>
          </div>
          <button className="text-[1.15rem] opacity-40 hover:opacity-100 transition-opacity" onClick={onClose}>✕</button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4" style={{ overscrollBehavior: "contain" }}>
          {/* Contacts & Location */}
          <div className="detail-section">
            <div className="detail-section-title">Contactos & Localização</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-3">
              <div>
                <div className="font-mono text-[0.57rem] uppercase tracking-wider" style={{ color: "var(--mv-blue-mid)" }}>Local</div>
                <div className="text-[0.84rem] font-semibold">{doctor.local || "-"}</div>
              </div>
              <div>
                <div className="font-mono text-[0.57rem] uppercase tracking-wider" style={{ color: "var(--mv-blue-mid)" }}>Especialidade</div>
                <div className="text-[0.84rem]">{doctor.espec || "-"}</div>
              </div>
              <div>
                <div className="font-mono text-[0.57rem] uppercase tracking-wider" style={{ color: "var(--mv-blue-mid)" }}>Zona</div>
                <div className="text-[0.84rem]">{doctor.zone || "-"}</div>
              </div>
              <div>
                <div className="font-mono text-[0.57rem] uppercase tracking-wider" style={{ color: "var(--mv-blue-mid)" }}>Dia Preferencial</div>
                <div className="text-[0.84rem]">{DAYS[Number(doctor.prefDay)] || "-"}</div>
              </div>
              <div>
                <div className="font-mono text-[0.57rem] uppercase tracking-wider" style={{ color: "var(--mv-blue-mid)" }}>Telefone</div>
                <div className="text-[0.84rem]">
                  {doctor.tel ? <a href={`tel:${doctor.tel}`} style={{ color: "var(--mv-blue)" }}>{doctor.tel}</a> : "-"}
                </div>
              </div>
              <div>
                <div className="font-mono text-[0.57rem] uppercase tracking-wider" style={{ color: "var(--mv-blue-mid)" }}>Email</div>
                <div className="text-[0.84rem]" style={{ wordBreak: "break-all" }}>
                  {doctor.email ? <a href={`mailto:${doctor.email}`} style={{ color: "var(--mv-blue)" }}>{doctor.email}</a> : "-"}
                </div>
              </div>
              {doctor.addr && (
                <div className="col-span-2">
                  <div className="font-mono text-[0.57rem] uppercase tracking-wider" style={{ color: "var(--mv-blue-mid)" }}>Morada</div>
                  <div className="text-[0.84rem]">{doctor.addr}</div>
                </div>
              )}
            </div>
          </div>

          {/* Tags + Score + Progress */}
          <div className="flex flex-wrap gap-[8px] justify-center">
            <span className={`ptag ptag-${doctor.prio}`}>{PRIO_LABELS[doctor.prio]} Prioridade</span>
            <span className={`pottag pottag-${doctor.pot}`}>{POT_LABELS[doctor.pot]} Potencial</span>
            <span className={`ctag ctag-${compliance.status === "ok" ? "ok" : compliance.status === "partial" ? "partial" : "none"}`}>
              {compliance.status === "ok" ? "Cumprido" : compliance.status === "partial" ? `${compliance.actual}/${compliance.target}` : "Sem visita"}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <ScoreRing score={score} barColor={barColor} pct={pct} />
            <div className="flex-1 min-w-0">
              <div className="text-[0.72rem] mb-[6px]" style={{ color: "var(--mv-ink-light)" }}>
                Meta: {compliance.target}× por período · {fmtDate(period.start)} → {fmtDate(period.end)}
              </div>
              <div className="flex items-center gap-[10px]">
                <div className="flex-1 h-[7px] rounded-full overflow-hidden" style={{ background: "var(--mv-border)" }}>
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: barColor, transition: "width 0.5s" }} />
                </div>
                <span className="font-mono text-[0.78rem] font-semibold">{compliance.actual}/{compliance.target}</span>
              </div>
              <div className="mt-2">
                <NextVisitBadge date={nextVisit} />
              </div>
            </div>
          </div>

          {/* Notes */}
          {doctor.notes && (
            <div className="detail-section" style={{ background: "var(--mv-green-light)", borderLeft: "3px solid var(--mv-green)" }}>
              <div className="detail-section-title">Notas</div>
              <div className="text-[0.82rem] leading-[1.5] mt-2">{doctor.notes}</div>
            </div>
          )}

          {/* Visit history */}
          <div className="detail-section">
            <div className="detail-section-title">Histórico de Visitas ({doctorVisits.length} total)</div>
            {doctorVisits.length === 0 ? (
              <div className="text-[0.78rem] mt-2" style={{ color: "var(--mv-ink-light)" }}>Sem visitas registadas.</div>
            ) : (
              <div className="mt-2">
                {doctorVisits.map((v, i) => {
                  const colors = ["var(--mv-green)", "var(--mv-blue-mid)", "var(--mv-blue)"];
                  const c = colors[Math.min(v.visitNumber, 3) - 1];
                  return (
                    <div key={i} className="flex gap-3 py-[10px]" style={{ borderBottom: i < doctorVisits.length - 1 ? "1px solid var(--mv-border)" : undefined }}>
                      <div className="w-[10px] h-[10px] rounded-full flex-shrink-0 mt-1" style={{ background: c }} />
                      <div className="font-mono text-[0.62rem] min-w-[70px] whitespace-nowrap" style={{ color: "var(--mv-ink-light)" }}>
                        {fmtDate(v.dateKey)} {v.time}
                      </div>
                      <div className="flex-1">
                        <div className="text-[0.72rem] font-semibold" style={{ color: c }}>{v.visitNumber}ª visita</div>
                        {v.comment ? (
                          <div className="text-[0.76rem] mt-[2px]">{v.comment}</div>
                        ) : (
                          <div className="text-[0.76rem] mt-[2px] italic" style={{ color: "var(--mv-ink-xlight)" }}>Sem comentário</div>
                        )}
                        {v.products.length > 0 && (
                          <div className="flex gap-[3px] flex-wrap mt-[3px]">
                            {v.products.map((p) => <span key={p} className="prod-tag">{p}</span>)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Apoios */}
          {doctorApoios.length > 0 && (
            <div className="detail-section">
              <div className="detail-section-title">Apoios Registados ({doctorApoios.length})</div>
              <div className="mt-2">
                {doctorApoios.map((a) => {
                  const dur = Math.round((new Date(a.dataFim).getTime() - new Date(a.dataInicio).getTime()) / 86400000) + 1;
                  return (
                    <div key={a.id} className="flex gap-3 py-[10px]" style={{ borderBottom: "1px solid var(--mv-border)" }}>
                      <div className="w-[10px] h-[10px] rounded-full flex-shrink-0 mt-1" style={{ background: "var(--mv-purple)" }} />
                      <div className="font-mono text-[0.62rem] min-w-[70px] whitespace-nowrap" style={{ color: "var(--mv-ink-light)" }}>
                        {fmtDate(a.dataInicio)}
                      </div>
                      <div className="flex-1">
                        <div className="text-[0.78rem] font-semibold" style={{ color: "var(--mv-ink-dark)" }}>{a.evento}</div>
                        <div className="flex gap-[6px] items-center mt-[3px] flex-wrap">
                          <span className={`apoio-tipo apoio-tipo-${a.tipo}`}>{APOIO_LABELS[a.tipo]}</span>
                          <span className="font-mono text-[0.58rem]" style={{ color: "var(--mv-ink-xlight)" }}>
                            {dur} dia{dur !== 1 ? "s" : ""} · até {fmtDate(a.dataFim)}
                          </span>
                        </div>
                        {a.notas && <div className="text-[0.76rem] mt-[3px]" style={{ color: "var(--mv-ink-light)" }}>{a.notas}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 flex items-center gap-3 px-5 py-3" style={{ borderTop: "1px solid var(--mv-border)" }}>
          {onDelete && (
            <button className="mv-btn mv-btn-danger mv-btn-sm" onClick={onDelete}>🗑 Eliminar</button>
          )}
          <div className="flex-1" />
          <button className="mv-btn mv-btn-ghost" onClick={onClose}>Fechar</button>
          <button className="mv-btn mv-btn-primary" onClick={onEdit}>✏ Editar</button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
